import React, { createContext, useContext, useMemo, lazy, Suspense } from 'react';
import { FiLock } from 'react-icons/fi';
import featureConfig from '../config/features';

const MonetizationOverlay = lazy(() => import('../components/MonetizationOverlay'));

// Flatten config → { flagName: boolean }
// This is the single place that interprets the config shape,
// so changing the config structure only requires updating this file.
const resolveFlags = (config) =>
  Object.fromEntries(
    Object.entries(config).map(([key, val]) => [
      key,
      typeof val === 'boolean' ? val : Boolean(val?.enabled),
    ])
  );

const FeatureContext = createContext(resolveFlags(featureConfig));

// ─── Provider ─────────────────────────────────────────────────────────────────
// Wrap your app once with <FeatureProvider>.
// In the future you can fetch server-side overrides here and merge them in,
// e.g. per-tenant or per-user flag overrides from the backend.
export const FeatureProvider = ({ children, overrides = {} }) => {
  const flags = useMemo(
    () => ({ ...resolveFlags(featureConfig), ...overrides }),
    // overrides is expected to be stable (passed from a parent useState / API call)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(overrides)]
  );

  return (
    <FeatureContext.Provider value={flags}>
      {children}
    </FeatureContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
// const isEnabled = useFeature('voiceCall');
export const useFeature = (name) => {
  const flags = useContext(FeatureContext);
  return Boolean(flags[name]);
};

// ─── Locked fallback (shows lock icon + upgrade link) ─────────────────────────
const LockedFallback = ({ feature }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.75rem 1rem', background: 'var(--bg-subtle)',
    border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text-muted)', fontSize: '0.8125rem', cursor: 'pointer',
  }}
    onClick={() => window.location.href = '/settings?tab=plan'}
  >
    <FiLock size={14} />
    <span>{feature} is locked — upgrade your plan</span>
  </div>
);

// ─── Declarative gate component ───────────────────────────────────────────────
// <FeatureGate feature="voiceCall">
//   <CallButton />
// </FeatureGate>
//
// Optional `fallback` prop renders when the feature is disabled:
// <FeatureGate feature="gif" fallback={<span>GIFs unavailable</span>}>
//   <GifPicker />
// </FeatureGate>
//
// mode="hidden"   (default) — renders fallback or null
// mode="locked"   — renders a lock icon with upgrade prompt (inline)
// mode="overlay"  — renders glassmorphism MonetizationOverlay with blurred children + CTA
export const FeatureGate = ({ feature, fallback, mode = 'hidden', children, planUrl }) => {
  const enabled = useFeature(feature);
  if (enabled) return children;
  if (mode === 'overlay') {
    return (
      <Suspense fallback={<LockedFallback feature={feature} />}>
        <MonetizationOverlay feature={feature} planUrl={planUrl}>
          {children}
        </MonetizationOverlay>
      </Suspense>
    );
  }
  if (mode === 'locked') return <LockedFallback feature={feature} />;
  return fallback || null;
};

export default FeatureContext;
